import React, { useEffect, useState } from 'react'
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControl, InputLabel, MenuItem, Select, TextField, Typography,
} from '@mui/material'
import { supabase } from '../../supabaseClient'
import { readFunctionError } from '../../lib/functionError'
import { UserRole } from '../../types'

interface StaffOption {
  user_id:    string
  first_name: string
  last_name:  string
  email:      string
  role:       UserRole
}

interface Props {
  open:               boolean
  studentId:          string | null
  studentLabel:       string
  currentMentorId?:   string | null
  currentMentorName?: string | null
  onClose:            () => void
  onAssigned:         () => void
}

const STAFF_ROLES: UserRole[] = ['admin', 'office_manager', 'instructor', 'mentor']

const ROLE_LABELS: Record<string, string> = {
  admin:          'Admin',
  office_manager: 'Manager',
  instructor:     'Instructor',
  mentor:         'Mentor',
}

export default function AssignMentorDialog({
  open, studentId, studentLabel, currentMentorId, currentMentorName, onClose, onAssigned,
}: Props) {
  const [staff,      setStaff]      = useState<StaffOption[]>([])
  const [mentorId,   setMentorId]   = useState('')
  const [context,    setContext]    = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error,      setError]      = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setMentorId(currentMentorId ?? '')
    setContext('')
    setError(null)
    supabase
      .from('users')
      .select('user_id, first_name, last_name, email, role')
      .in('role', STAFF_ROLES)
      .eq('status', 'active')
      .order('last_name')
      .then(({ data }) => setStaff(data ?? []))
  }, [open, currentMentorId])

  async function callAssign(mentorIdOrNull: string | null) {
    if (!studentId) return
    setSubmitting(true)
    setError(null)
    const { data, error: invokeErr } = await supabase.functions.invoke('assign-primary-mentor', {
      body: {
        student_id:     studentId,
        mentor_user_id: mentorIdOrNull,
        context:        context.trim() || undefined,
      },
    })
    if (invokeErr) {
      setError(await readFunctionError(invokeErr))
      setSubmitting(false); return
    }
    if (data?.error) {
      setError(data.error)
      setSubmitting(false); return
    }
    setSubmitting(false)
    onAssigned()
    onClose()
  }

  async function handleAssign() {
    if (!mentorId) return
    await callAssign(mentorId)
  }

  async function handleUnassign() {
    await callAssign(null)
  }

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        {currentMentorId ? 'Change Primary Mentor' : 'Assign Primary Mentor'}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {studentLabel}
        </Typography>
        {currentMentorName && (
          <Typography variant="body2" sx={{ mb: 2 }}>
            Current mentor: <strong>{currentMentorName}</strong>
          </Typography>
        )}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <FormControl size="small" fullWidth required sx={{ mb: 2 }}>
          <InputLabel>Primary Mentor</InputLabel>
          <Select
            label="Primary Mentor"
            value={mentorId}
            onChange={e => setMentorId(e.target.value)}
          >
            {staff.length === 0 && (
              <MenuItem value="" disabled>(no active staff)</MenuItem>
            )}
            {staff.map(s => {
              const fullName = `${s.first_name} ${s.last_name}`.trim() || s.email
              return (
                <MenuItem key={s.user_id} value={s.user_id}>
                  {fullName} <span style={{ color: 'rgba(0,0,0,0.5)', marginLeft: 8 }}>· {ROLE_LABELS[s.role]}</span>
                </MenuItem>
              )
            })}
          </Select>
        </FormControl>

        <TextField
          label="Note (optional)"
          size="small" fullWidth multiline rows={2}
          placeholder="Context for this assignment, e.g. 'Specialty match for biotech project'…"
          value={context}
          onChange={e => setContext(e.target.value)}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
        <Box>
          {currentMentorId && (
            <Button color="warning" onClick={handleUnassign} disabled={submitting}>
              Unassign
            </Button>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAssign}
            disabled={submitting || !mentorId || mentorId === currentMentorId}
          >
            {submitting ? <CircularProgress size={20} /> : (currentMentorId ? 'Change' : 'Assign')}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  )
}
